/**
 * What the empty stage should say about a selection the list cannot show yet.
 *
 * Owner, 2026-09-06 (screenshot from the phone): 現在每次新建立一個 Agent 或是 Fork 這個
 * Agent,會先跳出來這樣子的一個畫面,告訴你它這個 Session 已經不在了,這個是不對的一個行為.
 *
 * The screen said "That session isn't here any more" about a session that was three
 * seconds old. A fork drops a synthetic row into the cache and selects it; the
 * invalidation that follows replaces the cache with the machine's real roster, which
 * does not list the new session yet because its CLI has not finished starting — and
 * the stage's only test was "the list loaded, everyone answered, it is not there",
 * which is exactly what a session that is STARTING looks like. A create races the
 * same way when the machine is slow.
 *
 * So the stage is told what the app itself just did. A selection the app EXPECTS to
 * appear (it created or forked it moments ago) is "starting" until the roster lists
 * it or the expectation expires; a list still being re-asked is "resolving", never a
 * verdict; "missing" is reserved for the list having answered, everyone included,
 * with nothing expected and nothing found.
 *
 * Pure, so the four outcomes and their boundaries are pinned by tests instead of by
 * whoever reproduces the race next.
 */
export type StageState = 'welcome' | 'resolving' | 'starting' | 'missing'

/** How long a just-created or just-forked session may take to show up in the roster
 *  before the stage stops vouching for it. A slow machine starts a CLI in seconds;
 *  a full minute and a half is a machine that is not going to. */
const EXPECT_MS = 90_000

export type Expecting = { session: string; host: string | null; since: number }

export function stageState(args: {
  /** Something is selected and none of the lists resolve it. */
  unresolved: boolean
  /** The list the selection resolves against has ever arrived. */
  listLoaded: boolean
  /** Every machine that could hold the selection has answered this sweep. */
  askedEveryone: boolean
  /** The list is being re-asked right now. */
  fetching: boolean
  /** The app itself just created / forked this session, if any. */
  expecting: Expecting | null
  selected: { session: string; host: string | null } | null
  now: number
}): StageState {
  const { unresolved, listLoaded, askedEveryone, fetching, expecting, selected, now } = args
  if (!unresolved) return 'welcome'
  // A create knows only the session name (the host label arrives with the row), so
  // a null expected host matches any; a fork knows its host and holds it to that.
  const expected =
    !!expecting &&
    !!selected &&
    expecting.session === selected.session &&
    (expecting.host === null || expecting.host === (selected.host ?? null)) &&
    now - expecting.since < EXPECT_MS
  if (expected) return 'starting'
  if (!listLoaded || !askedEveryone || fetching) return 'resolving'
  return 'missing'
}
